import {
  ChangeDetectorRef,
  Renderer2,
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  DOCUMENT
} from "@angular/core";
import { Validators, FormGroup, FormControl } from "@angular/forms";
import { WebService } from "../services/web/web.service";
import { ToastService } from "../services/notification/toast.service";
import { SpinnerService } from "../services/spinner/spinner.service";
import { ActivatedRoute, Router } from "@angular/router";
import { DatePipe } from "@angular/common";
import { HttpStatusCode } from "@angular/common/http";
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { interval, Observable, of, timer, Subject } from "rxjs";
import { DomSanitizer } from "@angular/platform-browser";
import { catchError, map, switchMap, delay, takeWhile, takeUntil, timeout } from "rxjs/operators";
import { UniqueCodeService } from "./unique-code.service";
import { FacPopupComponent } from "../account-settings/plans/fac-popup/fac-popup.component";
import { environment } from "../../environments/environment";
import { AppConfirmService } from "../app-confirm/app-confirm.service";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ToastrService } from "ngx-toastr";
import { TableService } from "../services/pager/pager";
import { ReportFilter } from "../customer-invoices/report.filter";
import { SecurityService } from "../services/security.service";
import { CreditCardHelperService } from "../services/credit-card-helper.service";
import { TranslateService } from "@ngx-translate/core";
import { PlanType } from "../shared/enums/plan-type";
import { ExpirationYear, generateExpirationYears } from "../shared/utils/credit-card-expiration";
import { UserPermissionsService } from "../services/user-permissions.service";

// 1. Pega esto antes de tu @Component
function luhnValidator(control: import("@angular/forms").AbstractControl) {
  const value = (control.value || "").replace(/\D/g, "");
  if (!value) return null;
  let sum = 0;
  let shouldDouble = false;
  for (let i = value.length - 1; i >= 0; i--) {
    let digit = +value[i];
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0 ? null : { luhn: true };
}

// Valida la longitud de la tarjeta (13-19 dígitos). El required maneja el vacío.
function cardLengthValidator(control: import("@angular/forms").AbstractControl) {
  const value = (control.value || "").replace(/\D/g, "");
  if (!value) return null;
  return value.length >= 13 && value.length <= 19 ? null : { cardLength: true };
}

// Valida que la combinación Mes/Año de expiración no esté vencida.
function expiryNotExpiredValidator(group: import("@angular/forms").AbstractControl) {
  const month = group.get("expMonthCreditCard")?.value;
  const year = group.get("expYearCreditCard")?.value;
  if (!month || !year) return null;
  const m = parseInt(month, 10);
  let y = parseInt(year, 10);
  if (isNaN(m) || isNaN(y)) return null;
  if (y < 100) y += 2000; // El dropdown guarda el año en 2 dígitos (ej. "29" -> 2029)
  const now = new Date();
  if (y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth() + 1)) {
    return { expired: true };
  }
  return null;
}

@Component({
  selector: "app-add-client",
  templateUrl: "./add-client.component.html",
  styleUrls: ["./add-client.component.scss"],
  providers: [DatePipe],
  standalone: false,
})
export class AddClientComponent implements OnInit, OnDestroy {
  private static readonly EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private static readonly CUSTOMER_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{2,35}$/;
  private static readonly CUSTOMER_SEARCH_PATTERN = new RegExp(
    `${AddClientComponent.EMAIL_PATTERN.source}|${AddClientComponent.CUSTOMER_CODE_PATTERN.source}`,
  );
  private static readonly EMAIL_REQUIRED_ERROR = "required";
  private static readonly CUSTOMER_EMAIL_FIELD = "email_customer";
  private static readonly DUPLICATE_ONBOARDING_AUTH_USER_ERROR_CODE = "duplicate_onboarding_auth_user";
  private static readonly DUPLICATE_ONBOARDING_AUTH_USER_TRANSLATION_KEY = "addClient.messages.duplicateOnboardingAuthUser";
  private static readonly CUSTOMER_FIELDS_WITH_CONDITIONAL_READONLY = [
    "name_customer",
    "lastName_customer",
    "identifier_customer",
    AddClientComponent.CUSTOMER_EMAIL_FIELD,
    "phoneNumber_customer",
    "address",
  ] as const;
  private destroy$ = new Subject<void>();
  readonly PlanType = PlanType;
  mode: "subscription" | "onetime_payment" = "subscription";
  onetimePlanId: any;
  isPopupVisible: boolean = false; // Estado inicial del popup
  clientForm: FormGroup;
  readonly customerSearchControl = new FormControl("", [
    Validators.required,
    Validators.pattern(AddClientComponent.CUSTOMER_SEARCH_PATTERN),
  ]);
  paymentForm: FormGroup;
  public readonly expirationYears: ExpirationYear[] = generateExpirationYears();
  detectedCardType: string = "";
  cvvMaxLength: number = 3;
  plans: any;
  currencies: any;
  numbers: any;
  exist: Boolean = false;
  onboardingConfigID: any;
  provinces: any[] = [];
  onboardingConfigId: any;
  corregimientos: any[] = [];
  displayedColumns: string[] = ["select", "ID", "name_customer", "email_customer", "actions"];
  // PARA MANEJO DE PANEL DE BUSQUEDA
  isOpenSearchPanel = true;
  filter = new ReportFilter(this.tableService.filter);
  dataSource: any;
  items: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  get creditCardNumberMasked(): string {
    const value = this.paymentForm.controls["numberCreditCard"].value || "";
    return (
      value
        .replace(/\D/g, "")
        .match(/.{1,4}/g)
        ?.join("-") || ""
    );
  }

  // Este método va en tu clase AddClientComponent
  onCardInput(event: any): void {
    let raw = event.target.value.replace(/\D/g, "").substring(0, 19); // Solo números, hasta 19 dígitos
    let formatted = raw.match(/.{1,4}/g)?.join("-") || raw;
    this.paymentForm.controls["numberCreditCard"].setValue(raw, { emitEvent: false });
    event.target.value = formatted;

    // Detectar marca y ajustar la validación del CVV (Amex = 4 dígitos, resto = 3)
    this.detectedCardType = this.detectCardType(raw);
    this.cvvMaxLength = this.detectedCardType === "amex" ? 4 : 3;
    const cvv = this.paymentForm.controls["ccv"];
    cvv.setValidators([Validators.pattern(new RegExp(`^\\d{${this.cvvMaxLength}}$`))]);
    cvv.updateValueAndValidity({ emitEvent: false });
  }

  // Detecta la marca de la tarjeta a partir de los dígitos crudos (Visa / Amex / Mastercard).
  private detectCardType(raw: string): string {
    if (!raw) return "";
    if (/^4/.test(raw)) return "visa";
    if (/^3[47]/.test(raw)) return "amex";
    if (/^(5[1-5]|2(2[2-9]|[3-6][0-9]|7[01]|720))/.test(raw)) return "mastercard";
    return "";
  }

  // NECESARIOS PARA 3DS DINAMICO
  retryCount = 0;
  maxRetries = 5;
  uniqueCode: string = "";
  myIp: string = "189.127.166.102";
  myHeight: number;
  myWidth: number;
  jwtTokenForThird: string;
  transactionIdForThird: string;
  transactionId: string;
  iframeValue: string;
  iframeName: string;
  iframeUrl: string;
  redirectUrl: string;
  dataObject: any;
  private pollingSuccess = new Subject<void>();
  iframeElement: HTMLIFrameElement;
  hasPlanID: any;
  emailCustomer: any;

  // Variables para manejar tarjetas guardadas
  customerForCards: any = null;
  readonly customerFieldsReadOnly: Record<string, boolean> = {};
  tarjetasGuardadas: any[] = [];
  usarTarjetaGuardada: boolean = false;
  tarjetaSeleccionadaId: any = null;
  // false = solo se muestra el input de búsqueda (correo/código), centrado.
  // true = se revela el resto del formulario (lleno si el cliente existe,
  // vacío en modo creación si no existe) tras resolver search().
  mostrarFormularioCompleto: boolean = false;

  // Variables para modal de link de afiliación
  showLinkModal: boolean = false;
  subscriptionLink: string = "";
  linkCopied: boolean = false;

  togglePopup(isVisible: boolean): void {
    if (isVisible) {
      this.normalizeClientFields();
      const emailCustomerControl = this.clientForm.controls[AddClientComponent.CUSTOMER_EMAIL_FIELD];
      const emailErrors = emailCustomerControl.errors;
      if (emailErrors?.[AddClientComponent.EMAIL_REQUIRED_ERROR]) {
        const remainingEmailErrors = { ...emailErrors };
        delete remainingEmailErrors[AddClientComponent.EMAIL_REQUIRED_ERROR];
        emailCustomerControl.setErrors(
          Object.keys(remainingEmailErrors).length ? remainingEmailErrors : null,
        );
      }
    }
    if (isVisible && !this.canCreateCustomer()) {
      return this.toast.showWarning("No tienes permisos para crear clientes.");
    }
    if (isVisible && !this.canSuscribeCustomer()) {
      return this.toast.showWarning("No tienes permisos para suscribir clientes a planes.");
    }
    if (isVisible && !this.canGenerateCreditCardToken()) {
      return this.toast.showWarning("No tienes permisos para generar token de tarjeta.");
    }

    if (!this.clientForm.valid) {
      this.clientForm.markAllAsTouched();
      return this.toast.showInfo(this.translate.instant("addClient.messages.formIncomplete"));
    }

    if (isVisible) {
      // 👉 Abrir loader ANTES de todo (siempre se mostrará)
      this.spinner.open();

      // Buscar cliente por email actual y cargar sus tarjetas
      // Ahora esperamos a que termine antes de verificar duplicados
      this.buscarClienteYTarjetas().subscribe(
        (cliente) => {
          // buscarClienteYTarjetas() puede haber resincronizado el form completo con
          // datos de un cliente distinto (aplicarClienteAlFormulario) DESPUÉS del gate
          // de validez de arriba. Si ese cliente viene con formato que no pasa los
          // validadores estrictos del form, hay que frenar acá antes de abrir el popup
          // en cualquiera de sus ramas, en vez de dejar que el usuario se entere recién
          // al enviar.
          if (!this.clientForm.valid) {
            this.clientForm.markAllAsTouched();
            this.spinner.close();
            this.toast.showInfo(this.translate.instant("addClient.messages.formIncomplete"));
            return;
          }
          // Solo verificar duplicados si encontramos un cliente y estamos en modo subscription
          if (this.mode === "subscription" && cliente && this.clientForm.value.plan) {
            const planSeleccionado = +this.clientForm.value.plan;

            this.verificarPlanDuplicado(cliente.id, planSeleccionado).subscribe(
              (existePlan) => {
                this.spinner.close(); // 👈 Cerrar loader aquí
                if (existePlan) {
                  this.exist = true;
                  this.toast.showError(this.translate.instant("addClient.messages.duplicatePlan"));
                  console.log("⚠️ Plan duplicado detectado - Bloqueando apertura del modal");
                } else {
                  this.exist = false;
                  this.isPopupVisible = true;
                  console.log("✅ No hay plan duplicado - Abriendo modal");
                }
              },
              (error) => {
                this.spinner.close(); // 👈 Cerrar loader en error
                console.error("❌ Error verificando plan duplicado:", error);
                // En caso de error, permitir continuar
                this.exist = false;
                this.isPopupVisible = true;
              },
            );
          } else {
            this.spinner.close(); // 👈 Cerrar loader para cliente nuevo
            console.log("Cliente nuevo o modo onetime_payment - no hay riesgo de duplicado");
            // Cliente nuevo o modo onetime_payment - no hay riesgo de duplicado
            this.exist = false;
            this.isPopupVisible = true;
          }
        },
        (error) => {
          this.spinner.close(); // 👈 Cerrar loader en error de búsqueda
          // Error al buscar cliente, pero permitir abrir modal
          console.error("❌ Error al buscar cliente:", error);
          this.exist = false;
          this.isPopupVisible = true;
        },
      );
    } else {
      // Al cerrar el modal, limpiar datos de tarjetas
      this.limpiarDatosTarjetas();
      this.isPopupVisible = isVisible;
    }
  }

  setSearchPanelState(isOpen: boolean): void {
    this.isOpenSearchPanel = isOpen;
  }

  constructor(
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    public tableService: TableService<any>,
    private uniqueCodeService: UniqueCodeService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private creditCardHelper: CreditCardHelperService,
    // public dialogRef: MatDialogRef<FacPopupComponent>
    private activateRoute: ActivatedRoute,
    private router: Router,
    private spinner: SpinnerService,
    private webService: WebService,
    private toast: ToastService,
    private translate: TranslateService,
    private userPermissionsService: UserPermissionsService,
    private securityService: SecurityService,
  ) {
    this.clientForm = new FormGroup({
      name_customer: new FormControl("", [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^(?=.*[A-Za-z])[A-Za-z ]+$/),
      ]),
      lastName_customer: new FormControl("", [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^(?=.*[A-Za-z])[A-Za-z ]+$/),
      ]),
      identifier_customer: new FormControl("", [
        Validators.required,
        Validators.pattern(/^[A-Za-z0-9-]+$/),
      ]),
      email_customer: new FormControl("", [Validators.pattern(AddClientComponent.EMAIL_PATTERN)]),
      phoneNumber_customer: new FormControl("", [
        Validators.required,
        Validators.pattern(/^[0-9]{7,14}$/),
      ]),
      code_customer: new FormControl("", Validators.required),
      config: new FormControl(""),
      status_customer: new FormControl(1),
      service: new FormControl(""),
      plan: new FormControl("", Validators.required),
      partner: new FormControl(""),
      onboardingSuscription: new FormControl(true),
      address: new FormControl("", [Validators.required, Validators.pattern(/^[A-Za-z0-9 .,\-]+$/)]),
      province: new FormControl(""),
      corregimiento: new FormControl(""),
      payment_description: new FormControl(
        "",
        this.mode === "onetime_payment" ? Validators.required : [],
      ),
      amount: new FormControl("", this.mode === "onetime_payment" ? Validators.required : []),
      taxrate: new FormControl(
        "",
        this.mode === "onetime_payment"
          ? [Validators.required, Validators.min(0), Validators.max(100)]
          : [],
      ),
      start_date: new FormControl(new Date(), Validators.required),
      customAmount: new FormControl(""),
      customTaxRate: new FormControl(""),
    });
    this.paymentForm = new FormGroup(
      {
        nameHolderCreditCard: new FormControl("", [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(/^[\p{L} '.\-]+$/u),
        ]),
        numberCreditCard: new FormControl("", [
          Validators.required,
          luhnValidator,
          cardLengthValidator,
        ]),
        expMonthCreditCard: new FormControl("", Validators.required),
        expYearCreditCard: new FormControl("", [Validators.required]),
        ccv: new FormControl("", [Validators.pattern(/^\d{3}$/)]),
      },
      { validators: expiryNotExpiredValidator },
    );
    this.clientForm.addControl("amount", new FormControl(""));
  }

  list(event?: PageEvent) {
    this.spinner.open();
    this.items = [];
    this.tableService.filter = new ReportFilter(this.filter);
    let httpParams = this.webService.buildRequestParams(this.tableService.sort, "m", {
      pageIndex: event ? event.pageIndex : this.tableService.pager.pageIndex,
      pageSize: event ? event.pageSize : this.tableService.pager.pageSize,
    });

    httpParams = this.filter.getHttpParams(httpParams);

    // 🔵 Elegir el endpoint según el modo
    let url = "";
    if (this.mode === "onetime_payment") {
      url = this.webService.HOST + `/onboarding/plan/${this.onetimePlanId}/suscriptions/pending`;
    } else {
      url = this.webService.HOST + `/onboarding/${this.onboardingConfigID}/suscriptions/pending`;
    }

    this.webService.get(url, httpParams).subscribe(
      (response) => {
        // 🔵 IMPRIMIR CONSULTA COMPLETA
        console.log("🔵 Consulta al endpoint:", url);
        console.log("🔵 Query Params:", httpParams?.toString());
        console.log("🔵 Response recibida:", response);
        this.items = response.result;
        this.dataSource = new MatTableDataSource<any>(this.items);
        this.tableService.pager = response.pager;
        this.tableService.selection.clear();
        this.spinner.close();
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error.message);
        this.dataSource = [];
      },
    );
  }

  get selectedPlan(): any {
    const planId = +this.clientForm.value.plan;
    if (!planId || !this.plans) {
      return null;
    }
    return this.plans.find((p: any) => +p.id === planId) || null;
  }

  get isVariablePlan(): boolean {
    const plan = this.selectedPlan;
    return !!plan && +plan.type === PlanType.VARIABLE;
  }

  private applyVariablePlanValidators(): void {
    const customAmount = this.clientForm.get("customAmount");
    const customTaxRate = this.clientForm.get("customTaxRate");
    if (!customAmount || !customTaxRate) {
      return;
    }
    if (this.isVariablePlan) {
      customAmount.setValidators([Validators.required, Validators.min(0.01)]);
      customTaxRate.setValidators([Validators.min(0), Validators.max(100)]);
    } else {
      customAmount.clearValidators();
      customTaxRate.clearValidators();
      customAmount.setValue("", { emitEvent: false });
      customTaxRate.setValue("", { emitEvent: false });
    }
    customAmount.updateValueAndValidity({ emitEvent: false });
    customTaxRate.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    // this.dataSource.paginator = this.paginator;
    // this.dataSource.sort = this.sort;
    this.spinner.open();
    this.initializeDimensions();
    this.subscribeToPollingSuccess();
    this.clientForm
      .get("plan")!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyVariablePlanValidators());
    this.activateRoute.params.subscribe(
      (params) => {
        this.spinner.close();
        if (this.securityService.getTokenPartner() == null) {
          return;
        }
        this.clientForm.controls["config"].setValue(params["id"]);
        this.onboardingConfigID = params["id"];
        this.getConfigByID(params["id"]);
        // this.list();
        this.spinner.open();
        this.webService.get(this.webService.HOST + "/province").subscribe(
          (provinces) => {
            this.provinces = provinces.result.sort((a, b) => (a.name > b.name ? 1 : -1));
          },
          (err) => {
            this.spinner.close();
            this.toast.showError(err);
          },
        );
      },
      (err) => {
        this.spinner.close();
        this.toast.showError(err);
      },
    );
    this.activateRoute.queryParams.subscribe((params) => {
      if (params["mode"] === "onetime_payment") {
        this.mode = "onetime_payment";

        // Campos SOLO requeridos en cobro único
        this.clientForm.get("amount")?.setValidators([Validators.required]);
        this.clientForm
          .get("taxrate")
          ?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
        this.clientForm.get("plan")?.clearValidators();
        this.clientForm.get("start_date")?.clearValidators();
        this.clientForm.get("payment_description")?.setValidators([Validators.required]);
      } else {
        this.mode = "subscription";
        this.clientForm.get("amount")?.clearValidators();
        this.clientForm.get("taxrate")?.clearValidators();
        // Campos requeridos en suscripción tradicional
        this.clientForm.get("plan")?.setValidators([Validators.required]);
        this.clientForm.get("start_date")?.setValidators([Validators.required]);
        this.clientForm.get("payment_description")?.clearValidators();
      }
      this.clientForm.get("amount")?.updateValueAndValidity();
      this.clientForm.get("taxrate")?.updateValueAndValidity();
      this.clientForm.get("plan")?.updateValueAndValidity();
      this.clientForm.get("start_date")?.updateValueAndValidity();
      this.clientForm.get("payment_description")?.updateValueAndValidity();
    });
    // this.list();
  }

  goToImportCustomers(): void {
    if (!this.canCreateCustomer()) {
      this.toast.showWarning("No tienes permisos para crear clientes.");
      return;
    }

    this.router.navigate(["/portal", this.onboardingConfigID, "client", "import"]);
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  isAllSelected() {
    // return this.dataSource.every((client) => client.selected);
  }

  hasPartialSelection() {
    // return this.dataSource.some((client) => client.selected) && !this.isAllSelected();
  }

  hasSelectedUsers(): boolean {
    // return this.dataSource.some((client) => client.selected);
    return true;
  }

  toggleSelectAll(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.dataSource.forEach((client) => (client.selected = isChecked));
  }

  resendEmail(user: any) {
    this.spinner.open();
    let url = "";
    if (this.mode === "onetime_payment") {
      url = this.webService.HOST + "/customerHasPlan/" + user.id + "/uniquepay"; // <--- cambia aquí por tu endpoint real
    } else {
      url = this.webService.HOST + "/customerHasPlan/" + user.id;
    }
    this.webService.post({}, url).subscribe(
      (response) => {
        console.log("🔵 RESPONSE DE REENVIAR EMAIL:", response);
        console.log(`Correo reenviado a ${user.customerEmail}`);
        this.toastr.success(`Correo reenviado a ${user.customerEmail}`);
        this.ngOnInit();
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error.message);
      },
    );
  }

  async resendEmailsToSelected() {
    const selectedClients = this.tableService.selection.selected;
    if (selectedClients.length === 0) {
      this.toastr.info("No hay usuarios seleccionados para reenviar correos.");
      return;
    }
    this.spinner.open();
    try {
      const resendPromises = selectedClients.map((client) => {
        let url = "";
        if (this.mode === "onetime_payment") {
          url = this.webService.HOST + "/customerHasPlan/" + client.id + "/uniquepay"; // <--- endpoint para onetime
        } else {
          url = this.webService.HOST + "/customerHasPlan/" + client.id;
        }
        return this.webService.post({}, url).toPromise();
      });

      await Promise.all(resendPromises);
      this.spinner.close();
      this.toastr.success(`Correos reenviados a ${selectedClients.length} usuarios.`);
      this.ngOnInit();
    } catch (error) {
      this.spinner.close();
      // @ts-ignore
      this.toast.showError(error.message);
    }
  }

  async cancelSuscriptionsSelected() {
    const selectedClients = this.tableService.selection.selected;
    if (selectedClients.length === 0) {
      this.toastr.info("No hay usuarios seleccionados para reenviar correos.");
      return;
    }

    this.spinner.open();
    try {
      const cancelPromises = selectedClients.map((client) =>
        this.webService.put({}, this.webService.HOST + "/customerHasPlan/" + client.id).toPromise(),
      );

      await Promise.all(cancelPromises);
      this.spinner.close();
      this.toastr.success(`Cancelando suscripcion a ${selectedClients.length} usuarios.`);
      this.ngOnInit();
    } catch (error) {
      this.spinner.close();
      // @ts-ignore
      this.toast.showError(error.message);
    }
  }

  // Validador de Luhn como función

  cancelSubscription(user: any) {
    this.spinner.open();
    this.webService.put({}, this.webService.HOST + "/customerHasPlan/" + user.id).subscribe(
      (response) => {
        console.log(`Cancelando suscripción a ${user.customerEmail}`);
        this.spinner.close();
        this.toastr.success(`Suscripción cancelada a ${user.customerEmail}`);
        this.ngOnInit();
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error.message);
      },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 3DS DINAMICO
  ngAfterViewInit(): void {
    setTimeout(() => {
      const uniqueCode = this.generateUniqueCode();
      this.uniqueCodeService.setUniqueCode(uniqueCode);
      this.uniqueCode = uniqueCode;
      this.addUniqueCodeToHead(uniqueCode);
      this.addUniqueCodeToBody(uniqueCode);
      this.cdr.detectChanges();
    }, 0);
  }

  private addUniqueCodeToHead(uniqueCode: string): void {
    const comment = this.renderer.createComment("script head nico");
    this.renderer.appendChild(this.document.head, comment);
    const script = this.renderer.createElement("script");
    this.renderer.setAttribute(script, "type", "text/javascript");
    this.renderer.setAttribute(
      script,
      "src",
      `https://h.online-metrix.net/fp/tags.js?org_id=${environment.orgId}&session_id=bg_avocadoprime${uniqueCode}`,
    );
    this.renderer.appendChild(this.document.head, script);

    script.onload = () => console.log("Script loaded successfully.");
    script.onerror = () => console.error("Failed to load the script.");
  }

  private addUniqueCodeToBody(uniqueCode: string): void {
    const noscript = this.renderer.createElement("noscript");
    const iframe = this.renderer.createElement("iframe");
    this.renderer.setStyle(iframe, "width", "100px");
    this.renderer.setStyle(iframe, "height", "100px");
    this.renderer.setStyle(iframe, "border", "0");
    this.renderer.setStyle(iframe, "position", "absolute");
    this.renderer.setStyle(iframe, "top", "-5000px");
    this.renderer.setAttribute(
      iframe,
      "src",
      `https://h.online-metrix.net/fp/tags?org_id=${environment.orgId}&session_id=bg_avocadoprime${uniqueCode}`,
    );
    this.renderer.appendChild(noscript, iframe);
    this.renderer.appendChild(this.document.body, noscript);
  }

  private generateUniqueCode(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private initializeDimensions(): void {
    this.myHeight = window.innerHeight;
    this.myWidth = window.innerWidth;
  }

  private subscribeToPollingSuccess(): void {
    this.pollingSuccess.subscribe(() => this.continueToNextStep());
  }

  onProvinceChange() {
    this.corregimientos = [];
    this.corregimientos =
      this.provinces[
        this.provinces.findIndex((t) => t.id == Number(this.clientForm.value.province))
      ].corregimientos;
    this.corregimientos.sort((a, b) => (a.name > b.name ? 1 : -1));
  }

  getConfigByID(id: any) {
    const partner = this.securityService.getTokenPartner();
    if (partner == null) {
      this.spinner.close();
      return;
    }
    this.webService
      .get(this.webService.HOST + "/onboardingConfig/" + id + "?partner=" + partner)
      .subscribe(
        (config) => {
          this.spinner.close();
          this.plans = config.result.plans
            .filter(
              (p: { status_plan: number | any[]; type: number | any }) =>
                p.status_plan == 1 &&
                +p.type !== PlanType.LINK_DE_PAGO &&
                +p.type !== PlanType.ON_DEMAND,
            )
            .sort(
              (
                a: { name_plan: string },
                b: {
                  name_plan: string;
                },
              ) => a.name_plan.localeCompare(b.name_plan),
            );
          this.clientForm.controls["partner"].setValue({ id: +config.result.partner.id });
          this.clientForm.controls["service"].setValue(+config.result.service.id);
          // console.log("CONFIG recibida:", config.result);

          if (config.result.oneTimePlanId) {
            this.onetimePlanId = config.result.oneTimePlanId;
            console.log("🟢 oneTimePlanId obtenido:", this.onetimePlanId);
          }
          this.list();
        },
        (err) => {
          this.spinner.close();
          this.toast.showError(err);
        },
      );
  }

  // Normaliza (trim) email, nombre y apellido del cliente antes de validar/enviar al backend.
  private normalizeClientFields(): void {
    ["email_customer", "name_customer", "lastName_customer"].forEach((name) => {
      const ctrl = this.clientForm.get(name);
      if (ctrl && typeof ctrl.value === "string") {
        const trimmed = ctrl.value.trim();
        if (trimmed !== ctrl.value) {
          ctrl.setValue(trimmed, { emitEvent: false });
        }
      }
    });
  }

  // Traduce los mensajes de error del backend (validación de tarjeta, email, teléfono,
  // excepciones de negocio) a un texto legible en el idioma de la UI.
  // Primer patrón que coincide gana. Si nada coincide, respeta el mensaje original.
  private translateBackendError(err: any): string {
    const raw = typeof err === "string" ? err : (err?.message ?? String(err ?? ""));
    if (!raw) {
      return raw;
    }
    const rules: { pattern: RegExp; key: string }[] = [
      // Número de tarjeta
      { pattern: /numbercreditcard.*required/i, key: "numberRequired" },
      { pattern: /numbercreditcard must be 13 to 19|13 to 19 digits/i, key: "numberLength" },
      // Nombre del tarjetahabiente
      { pattern: /nameholdercreditcard.*required/i, key: "nameRequired" },
      { pattern: /first and last name/i, key: "nameFull" },
      { pattern: /at most 50 characters/i, key: "nameMaxLength" },
      { pattern: /apostrophes/i, key: "namePattern" },
      // Mes de expiración
      { pattern: /expmonthcreditcard.*required/i, key: "monthRequired" },
      { pattern: /expiration month|between 01 and 12/i, key: "monthInvalid" },
      // Año de expiración
      { pattern: /expyearcreditcard.*required/i, key: "yearRequired" },
      {
        pattern: /expiration year|2-digit format|2-digit value greater than or equal/i,
        key: "yearInvalid",
      },
      // CVV (el "solo numérico" debe ir antes que el genérico de CVV)
      { pattern: /only numeric values are allowed/i, key: "cvvNumeric" },
      { pattern: /3 or 4 digits|invalid cvv/i, key: "cvvDigits" },
      // Email / teléfono / member
      { pattern: /invalid email format/i, key: "emailInvalid" },
      { pattern: /phone must be numeric|phonenumber_customer/i, key: "phoneInvalid" },
      { pattern: /'member'.*requerido/i, key: "memberRequired" },
      // Excepciones de negocio (el backend devuelve un texto en inglés similar al código).
      // Se usa \s+ en "credit card" para no chocar con nombres de campo como numberCreditCard.
      { pattern: /provider\s+not\s*found/i, key: "providerNotFound" },
      { pattern: /token\s+(is\s+)?duplicat/i, key: "tokenDuplicated" },
      { pattern: /credit\s+card\s+expired|expired\s+credit\s+card/i, key: "cardExpiredException" },
      {
        pattern: /credit\s+card\s+not\s+valid|invalid\s+credit\s+card|credit\s+card\s+invalid/i,
        key: "cardInvalid",
      },
      { pattern: /partner\s+not\s*found/i, key: "partnerNotFound" },
      { pattern: /customer\s+not\s*found/i, key: "customerNotFound" },
    ];
    for (const rule of rules) {
      if (rule.pattern.test(raw)) {
        return this.translate.instant("addClient.backendErrors." + rule.key);
      }
    }
    // Lo que no se puede traducir se muestra tal cual lo envía el backend.
    return raw;
  }

  send(emailSuscription: boolean) {
    this.clearCustomerSearch();

    // Normaliza (trim) email, nombre y apellido antes de validar y enviar al backend
    this.normalizeClientFields();
    const emailCustomerControl = this.clientForm.controls["email_customer"];
    if (emailSuscription && !emailCustomerControl.value) {
      emailCustomerControl.setErrors({
        ...emailCustomerControl.errors,
        [AddClientComponent.EMAIL_REQUIRED_ERROR]: true,
      });
    } else {
      emailCustomerControl.updateValueAndValidity({ emitEvent: false });
    }

    if (!this.canCreateCustomer()) {
      return this.toast.showWarning("No tienes permisos para crear clientes.");
    }
    if (!this.canSuscribeCustomer()) {
      return this.toast.showWarning("No tienes permisos para suscribir clientes a planes.");
    }
    if (!emailSuscription && !this.canGenerateCreditCardToken()) {
      return this.toast.showWarning("No tienes permisos para generar token de tarjeta.");
    }

    if (!this.clientForm.valid) {
      this.clientForm.markAllAsTouched();
      return this.toast.showInfo("Por favor complete el formulario de Datos del cliente");
    }
    if (
      this.mode === "onetime_payment" &&
      (!this.clientForm.value.amount || +this.clientForm.value.amount <= 0)
    ) {
      return this.toast.showInfo("Por favor ingrese el monto a cobrar.");
    }
    if (
      this.mode === "subscription" &&
      this.isVariablePlan &&
      (!this.clientForm.value.customAmount || +this.clientForm.value.customAmount <= 0)
    ) {
      return this.toast.showInfo(
        "Para planes variables debe ingresar un monto personalizado mayor a 0.",
      );
    }

    // Validación de pago solo si NO es por email
    if (!emailSuscription) {
      // Si está en el tab de "usar tarjeta guardada"
      if (this.usarTarjetaGuardada) {
        // Solo validar que tenga una tarjeta seleccionada
        if (!this.tarjetaSeleccionadaId) {
          return this.toast.showInfo("Por favor seleccione una tarjeta guardada");
        }
      } else {
        // Si está en el tab de "nueva tarjeta", validar formulario completo
        if (!this.paymentForm.valid) {
          this.paymentForm.markAllAsTouched();
          return this.toast.showInfo("Por favor complete el formulario de Datos del pago");
        }
      }
    }

    this.spinner.open();
    this.normalizeCustomerNameFields();

    // 🔍 CURL para Postman - Crear/Actualizar Cliente
    // const url = this.webService.HOST + "/onboarding/user";
    // const body = JSON.stringify(this.clientForm.value);
    // const curlCommand = `curl -X POST '${url}' -H 'Content-Type: application/json' -d '${body}'`;

    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('📤 CURL - Copiar y pegar en terminal:');
    // console.log(curlCommand);
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('📦 Body formateado:', JSON.parse(body));
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    this.webService
      .postPreservingHttpError(this.clientForm.value, this.webService.HOST + "/onboarding/user")
      .subscribe(
        (response) => {
          // console.log("RESPONSE DE CREAR USUARIO: ", response)
          let address: any = {};
          address.name = this.clientForm.value.name;
          address.lastname = this.clientForm.value.lastname;
          address.address = this.clientForm.value.address;
          address.province = this.clientForm.value.province;
          address.corregimiento = this.clientForm.value.corregimiento;

          let start_date = this.datePipe.transform(this.clientForm.value.start_date, "yyyy-MM-dd"); // TOMAMOS LA FECHA DE INICIO DE LA SUSCRIPCION
          let subscription: any = {
            start_suscription_date: start_date,
            suscribeByEmail: emailSuscription,
            onboardingClientSuscription: !emailSuscription,
            address: address,
            param: 8,
            customer: +response.result.id,
          };

          // Campos según el modo
          if (this.mode === "subscription") {
            subscription.plan = +this.clientForm.value.plan;
            if (this.isVariablePlan) {
              subscription.customAmount = +this.clientForm.value.customAmount;
              const rawTaxRate = this.clientForm.value.customTaxRate;
              subscription.customTaxRate =
                rawTaxRate === "" || rawTaxRate === null || rawTaxRate === undefined
                  ? null
                  : +rawTaxRate;
            }
          }
          if (this.mode === "onetime_payment") {
            subscription.amount = +this.clientForm.value.amount;
            subscription.taxRate = parseInt(this.clientForm.value.taxrate, 10);
            subscription.plan = this.onetimePlanId;
            subscription.mode = this.mode;
            subscription.payment_description = this.clientForm.value.payment_description;
          }

          // Agrega creditcard solo si es pago inmediato (NO por email)
          if (!emailSuscription) {
            // Verificar si usa tarjeta guardada
            if (this.usarTarjetaGuardada && this.tarjetaSeleccionadaId) {
              // Validar que la tarjeta existe
              const tarjetaSeleccionada = this.tarjetasGuardadas.find(
                (t) => t.id === this.tarjetaSeleccionadaId,
              );

              if (!tarjetaSeleccionada) {
                this.spinner.close();
                this.toast.showError("Por favor seleccione una tarjeta válida");
                return;
              }

              // Enviar el ID de la tarjeta guardada en el objeto creditcard
              subscription.creditcard = {
                id: tarjetaSeleccionada.token_creditCardToken,
                isManuallyAdded: true,
              };
              console.log("💳 Usando tarjeta guardada:", {
                id: tarjetaSeleccionada.token_creditCardToken,
                mask: tarjetaSeleccionada.mask_card || tarjetaSeleccionada.maskCreditCard,
                titular: tarjetaSeleccionada.nameHolderCreditCard,
              });
            } else {
              // Usar nueva tarjeta (flujo actual)
              subscription.creditcard = this.paymentForm.value;
              console.log("🆕 Usando nueva tarjeta");
            }
          }

          // console.log(`Método ejecutado: send`);
          // console.log(`Modo actual: ${this.mode}`);
          // console.log("🔵 Payload subscription a enviar:", subscription);
          // console.log(JSON.stringify(subscription, null, 2));
          // console.log(JSON.stringify(this.clientForm.value, null, 2));
          console.log("🔵 Payload subscription a enviar:", subscription);

          this.webService.post(subscription, this.webService.HOST + "/suscription").subscribe(
            (response) => {
              this.spinner.close();
              console.log("RESPONSE DE CREAR SUSCRIPCION: ", response);

              // ✅ Usar la URL real que devuelve el backend
              this.subscriptionLink = response.url || "";

              console.log("📧 Email enviado exitosamente");
              console.log("🔗 Link recibido del backend:", this.subscriptionLink);

              // Mostrar modal con el link si es envío por email
              if (emailSuscription) {
                this.showLinkModal = true;
                this.linkCopied = false;
              }

              this.toast.showSuccess("Suscripcion procesada correctamente");

              // Limpiar formularios y actualizar vista
              this.resetFormularios();
              this.isPopupVisible = false; // Cerrar modal de pago si estaba abierto
              this.list(); // Actualizar tabla de clientes pendientes

              // this.goBack(); // ❌ Comentado - No redirigir, mantener en la vista
              // this.handleResponse(response)
            },
            (err) => {
              this.spinner.close();
              this.toast.showError(this.translateBackendError(err));
            },
          );
        },
        (err) => {
          this.spinner.close();
          if (this.isDuplicateOnboardingAuthUserError(err)) {
            this.toast.showError(this.translate.instant(AddClientComponent.DUPLICATE_ONBOARDING_AUTH_USER_TRANSLATION_KEY));
            return;
          }
          console.log("❌ Error al crear/actualizar cliente:", err);

          // Interceptar error específico del teléfono y mostrar mensaje más legible
          const errorMessage = err?.error?.message ?? err?.error ?? err;
          if (
            errorMessage ===
            "Invalid parameter [phoneNumber_customer debe tener entre 7 y 14 digitos]"
          ) {
            this.toast.showError(
              "El número de teléfono debe contener entre 7 y 14 dígitos. Por favor, verifica el formato.",
            );
          } else {
            this.toast.showError(this.translateBackendError(errorMessage));
          }
        },
      );
  }

  private isDuplicateOnboardingAuthUserError(error: any): boolean {
    return error?.status === HttpStatusCode.Conflict
      && error?.error?.code === AddClientComponent.DUPLICATE_ONBOARDING_AUTH_USER_ERROR_CODE;
  }

  canCreateCustomer(): boolean {
    return this.userPermissionsService.hasPermission(
      "controllers.OnboardingConfigs.createOnboardingUser",
    );
  }

  canSuscribeCustomer(): boolean {
    return this.userPermissionsService.hasPermission(
      "controllers.HasPlans.suscribeCustomerToPlanWithTokenTDC",
    );
  }

  canGenerateCreditCardToken(): boolean {
    return this.userPermissionsService.hasPermission(
      "controllers.CreditCards.generateCreditcardToken",
    );
  }

  // Métodos para el modal de link de afiliación
  closeLinkModal(): void {
    this.showLinkModal = false;
    this.linkCopied = false;
    this.subscriptionLink = "";
  }

  resetFormularios(): void {
    // Limpiar formulario de cliente (excepto partner y service que son del config)
    const currentPartner = this.clientForm.value.partner;
    const currentService = this.clientForm.value.service;
    const currentConfig = this.clientForm.value.config;

    this.clientForm.reset({
      name_customer: "",
      lastName_customer: "",
      identifier_customer: "",
      email_customer: "",
      phoneNumber_customer: "",
      code_customer: "",
      config: currentConfig,
      status_customer: 1,
      service: currentService,
      plan: "",
      partner: currentPartner,
      onboardingSuscription: true,
      address: "",
      province: "",
      corregimiento: "",
      payment_description: "",
      amount: "",
      taxrate: "",
      start_date: new Date(),
      customAmount: "",
      customTaxRate: "",
    });

    // Limpiar formulario de pago
    this.paymentForm.reset({
      nameHolderCreditCard: "",
      numberCreditCard: "",
      expMonthCreditCard: "",
      expYearCreditCard: "",
      ccv: "",
    });

    // Limpiar datos de tarjetas guardadas
    this.limpiarDatosTarjetas();

    // Vuelve a mostrar solo el input de búsqueda para el próximo cliente.
    this.mostrarFormularioCompleto = false;
    this.setSearchPanelState(false);
  }

  copyLinkToClipboard(): void {
    navigator.clipboard
      .writeText(this.subscriptionLink)
      .then(() => {
        this.linkCopied = true;
        this.toast.showSuccess("Link copiado al portapapeles");

        // Resetear el estado después de 3 segundos
        setTimeout(() => {
          this.linkCopied = false;
        }, 3000);
      })
      .catch((err) => {
        console.error("Error al copiar:", err);
        this.toast.showError("Error al copiar el link");
      });
  }

  shareViaWhatsApp(): void {
    // Copiar el link primero
    this.copyLinkToClipboard();

    // Abrir WhatsApp con el link
    const message = encodeURIComponent(
      `Hola, aquí está tu link de afiliación: ${this.subscriptionLink}`,
    );
    const whatsappUrl = `https://wa.me/?text=${message}`;

    window.open(whatsappUrl, "_blank");
  }

  copyLinkFromTable(user: any): void {
    // Validar que la URL exista
    if (!user.url) {
      this.toast.showError("No se encontró el link de afiliación para este cliente");
      return;
    }

    // Asignar la URL del usuario al subscriptionLink
    this.subscriptionLink = user.url;

    // Abrir el modal
    this.showLinkModal = true;
    this.linkCopied = false;

    console.log("🔗 Abriendo modal con URL de la tabla:", user.url);
  }

  private createAddressFromForm() {
    return {
      name: this.clientForm.value.name,
      lastname: this.clientForm.value.lastname,
      address: this.clientForm.value.address,
      province: this.clientForm.value.province,
      corregimiento: this.clientForm.value.corregimiento,
    };
  }

  goBack() {
    this.router.navigate(["/my-portals"]);
  }

  omit_special_char(event: { charCode: any }) {
    var k;
    k = event.charCode; //         k = event.keyCode;  (Both can be used)
    return (k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || k == 32 || (k >= 48 && k <= 57);
  }

  onlyLettersAndSpacesKey(event: KeyboardEvent) {
    return /^[A-Za-z ]$/.test(event.key);
  }

  private normalizeCustomerNameFields(): void {
    ["name_customer", "lastName_customer"].forEach((controlName) => {
      const control = this.clientForm.get(controlName);
      const value = control?.value;
      if (typeof value === "string") {
        control?.setValue(value.trim().replace(/\s+/g, " "), { emitEvent: false });
      }
    });
  }

  private markInvalidCustomerSearchFieldsAsTouched(): void {
    ["name_customer", "lastName_customer", "address"].forEach((controlName) => {
      const control = this.clientForm.get(controlName);
      control?.updateValueAndValidity({ emitEvent: false });
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  onlyNumberKey(event: any) {
    return event.charCode == 8 || event.charCode == 0
      ? null
      : event.charCode >= 48 && event.charCode <= 57;
  }

  myLoadEvent() {
    this.spinner.open();
    this.webService.get(this.webService.HOST + "/hasplan/" + this.hasPlanID).subscribe(
      (chp) => {
        this.spinner.close();
        if (chp.result.status_customerPlan != 4) {
          if (chp.result.status_customerPlan == 1) {
            // ESTO ES QUE SE COBRO Y TODO BIEN CON LA SUSCRIPCION ESTA ACTIVA
            this.emailCustomer = chp.result.customer.email_customer;
            this.router.navigateByUrl("/congratulation");
          } else {
            this.toast.showError("Su pago fue rechazado por el banco");
          }
        }
      },
      (err) => {
        this.spinner.close();
        this.toast.showError(err);
      },
    );
  }

  // 3DS DINAMICO
  private handleResponse(response: any): void {
    console.log("RESPONSE DEL PROCESO 3DS: ", response);
    this.hasPlanID = response.id;

    if (response.bank === "powerTranz") {
      console.log("POWERTRANZ");
      this.hasPlanID = response.id;
      this.handlePowerTranzResponse(response);
    } else if (response.bank === "cyberSource") {
      console.log("CYBERSOURCE");
      this.handleCyberSourceResponse(response);
    } else if (response.bank === "emetec") {
      console.log("EMETEC");
      this.handleEmetecResponse(response);
    } else {
      if (response.message == "Successfull created") {
        // LA TARJETA PUEDE ESTAR VALIDA PARA 3DS
        this.hasPlanID = response.result;
        this.myLoadEvent();
      } else {
        this.toast.showError("ESTE BANCO NO ESTA REGISTRADO");
      }
    }
  }

  // TERMINA PROCESO DE 3DS DINAMICO

  // PROCESO PARA PROWERTRANZ
  private handlePowerTranzResponse(response: any): void {
    if (response.htmlFormData) {
      this.spinner.close();
      this.openFacPopup(response.htmlFormData);
    } else {
      this.hasPlanID = response.result;
      // this.emailCustomer = this.form.value.email;
      this.myLoadEvent();
    }
  }

  private openFacPopup(htmlFormData: string): void {
    const dialogRef = this.dialog.open(FacPopupComponent, {
      width: "50%",
      disableClose: true,
      data: { form: this.sanitizer.bypassSecurityTrustHtml(htmlFormData), id: this.hasPlanID },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.myLoadEvent();
    });
  }

  // TERMINA PROCESO PARA POWERTRANZ

  // PROCESO PARA CYBERSOURCE
  private handleCyberSourceResponse(response: any): void {
    this.spinner.open();
    if (response.sopResponse.responseCodeDescription !== "COMPLETED") {
      this.spinner.close();
      this.toast.showError("ERROR - PAYER AUTH DISTINTO DE COMPLETE");
      return;
    }

    const transactionIdentifier = response.sopResponse.transactionId;
    const jwtToken = response.sopResponse.auth3DSDetail.token;
    this.uniqueCodeService.setTransactionIdentifier(transactionIdentifier);
    this.createAndSubmitIframeForm(
      jwtToken,
      "https://centinelapistag.cardinalcommerce.com/V1/Cruise/Collect",
      "ddc-iframe",
      "ddc-form",
    );

    setTimeout(() => {
      const updatedData = this.transactionenrollment();
      const json = { chp: response.id, sop: updatedData, call: "enrollment" };
      console.log("CON ESTO HACEMOS EL ENROLLMENT: ", json);
      this.webService.post(json, `${this.webService.HOST}/sop`).subscribe(
        (enrollment) => {
          this.handleEnrollmentResponse(enrollment);
        },
        (err) => {
          this.spinner.close();
          console.error("ERROR LLAMANDO A ENRONLLMENT: ", err);
        },
      );
    }, 10000);
  }

  private handleEnrollmentResponse(enrollment: any): void {
    console.log("RESPUESTA DEL ENROLLMENT: ", enrollment);

    switch (enrollment.responseCodeDescription) {
      case "AUTHENTICATION_SUCCESSFUL":
        console.log("Successful Frictionless Authentication");
        this.handleEnrollmentSuccess(enrollment);
        break;

      case "AUTHENTICATION_FAILED":
        console.log("Unsuccessful Frictionless Authentication");
        this.spinner.close();
        this.toast.showError("AUTHENTICATION_FAILED");
        break;

      case "PENDING_AUTHENTICATION":
        console.log("Pending Authentication - Step-Up Required");
        this.handlePendingAuthentication(enrollment);
        break;

      default:
        console.log("Unexpected response code from enrollment");
        this.spinner.close();
        this.toast.showError(
          "Unexpected response code from enrollment : " + enrollment.responseCodeDescription,
        );
        break;
    }
  }

  private handlePendingAuthentication(secondResponse: any): void {
    this.spinner.open();
    console.log("SE REALIZA PROCESO DE 3DS YA QUE ENROLLMENT RETORNO PENDING_AUTHENTICATION");
    this.startPolling();

    this.jwtTokenForThird = secondResponse.auth3DSDetail.htmlCode;
    this.transactionIdForThird = secondResponse.transactionId;
    console.log("htmlCode para el 3DS:", this.jwtTokenForThird);

    if (
      secondResponse.extraData.veresEnrolled == "Y" &&
      secondResponse.extraData.paresStatus == "C"
    ) {
      console.log("Successful Step-Up Authentication");
    }

    this.createAndSubmitStepUpForm(this.jwtTokenForThird);
  }

  createAndSubmitStepUpForm(jwtToken: string): void {
    console.log("Iniciando StepUp para 3DS");

    const iframe = document.createElement("iframe");
    iframe.name = "step-up-iframe";
    iframe.height = "900";
    iframe.width = "900";
    iframe.style.display = "block";
    iframe.style.zIndex = "9999";
    iframe.style.top = "0";
    iframe.style.position = "fixed";
    iframe.style.background = "white";

    document.body.appendChild(iframe);
    this.iframeElement = iframe; // Guardar referencia al iframe

    console.log("Iframe para StepUp creado:", iframe);

    const form = document.createElement("form");
    form.id = "step-up-form";
    form.target = "step-up-iframe";
    form.method = "post";
    form.action = "https://centinelapistag.cardinalcommerce.com/V2/Cruise/StepUp";

    const inputJWT = document.createElement("input");
    inputJWT.type = "hidden";
    inputJWT.name = "JWT";
    inputJWT.value = jwtToken;
    form.appendChild(inputJWT);

    const inputMD = document.createElement("input");
    inputMD.type = "hidden";
    inputMD.name = "MD";
    inputMD.value = "optionally_include_custom_data_that_will_be_returned_as_is";
    form.appendChild(inputMD);

    document.body.appendChild(form);
    console.log("Formulario de StepUp creado y agregado al DOM:", form);

    form.submit();
    console.log("Formulario de StepUp enviado");

    iframe.onload = () => {
      console.log("Iframe StepUp loaded and displayed.");
    };
    this.spinner.close();
    window.addEventListener(
      "message",
      (event) => {
        if (event.origin === "https://centinelapistag.cardinalcommerce.com") {
          console.log("Mensaje StepUp recibido desde:", event.origin);
          const data = JSON.parse(event.data);
          console.log("Mensaje StepUp recibido:", data);
        }
      },
      false,
    );
  }

  private transactionenrollment(additionalData: any = {}): any {
    return {
      creditCard: {
        cardholderName: this.paymentForm.value.nameHolderCreditCard,
        creditCardDate:
          this.paymentForm.value.expYearCreditCard + this.paymentForm.value.expMonthCreditCard,
        cvv: this.paymentForm.value.ccv,
      },
      transactionIdentifier: this.uniqueCodeService.getTransactionIdentifier(),
      currency: "USD",
      extraData: {
        ip: this.myIp,
        fingerprint: this.uniqueCode,
        screenHeight: this.myHeight,
        screenWidth: this.myWidth,
        email: this.securityService.getTokenSub(),
        ...additionalData,
      },
      auth: additionalData.auth,
    };
  }

  startPolling(): void {
    this.spinner.open();
    const url = `${this.webService.HOST}/hasplan/${this.hasPlanID}`;
    let polling = true;

    interval(3000)
      .pipe(
        takeWhile(() => polling),
        switchMap(() =>
          this.webService.get(url).pipe(
            catchError((error) => {
              if (error.status === 404) {
                this.spinner.close();
                console.log("Respuesta 404: No encontrado");
                return of(null);
              } else {
                this.spinner.close();
                console.error("Error en la solicitud:", error);
                return of(null);
              }
            }),
          ),
        ),
      )
      .subscribe((response) => {
        if (response && response?.result) {
          console.log("RESPUESTA", response);

          if (response.result.status_customerPlan == 100) {
            this.spinner.close();
            polling = false;
            this.pollingSuccess.next();
            this.removeIframe();
          }
        }
      });
  }

  private handleEnrollmentSuccess(secondResponse: any): void {
    this.spinner.open();
    const fourthData = this.createTransactionData(secondResponse.transactionId, {
      auth: {
        authenticationTransactionId: secondResponse.transactionId,
      },
      eci: secondResponse.extraData.eci,
      eciRaw: secondResponse.extraData.eciRaw,
      cavv: secondResponse.extraData.cavv,
      paresStatus: secondResponse.extraData.paresStatus,
      veresEnrolled: secondResponse.extraData.veresEnrolled,
      xid: secondResponse.extraData.xid,
      authenticationTransactionId: secondResponse.extraData.authenticationTransactionId,
      acsTransactionId: secondResponse.extraData.acsTransactionId,
      ecommerceIndicator: secondResponse.extraData.ecommerceIndicator,
      specificationVersion: secondResponse.extraData.specificationVersion,
      directoryServerTransactionId: secondResponse.extraData.directoryServerTransactionId,
      ucafAuthenticationData: secondResponse.extraData.ucafAuthenticationData,
      ucafCollectionIndicator: secondResponse.extraData.ucafCollectionIndicator,
    });

    const json = { chp: this.hasPlanID, sop: fourthData, call: "sale" };
    console.log("CON ESTO HACEMOS EL SALE : ", json);
    this.webService.post(json, `${this.webService.HOST}/sop`).subscribe(
      (fourthResponse) => {
        console.log("RESPUESTA DEL SALE: ", fourthResponse);
        this.myLoadEvent();
        this.spinner.close();
      },
      (errr) => {
        console.error(errr);
        this.toast.showError(errr);
      },
    );
  }

  private createAndSubmitIframeForm(
    jwtToken: string,
    actionUrl: string,
    iframeName: string,
    formId: string,
  ): void {
    const iframe = document.createElement("iframe");
    iframe.name = iframeName;
    iframe.height = "900";
    iframe.width = "900";
    iframe.style.display = "none";
    document.body.appendChild(iframe);
    this.iframeElement = iframe;

    const form = document.createElement("form");
    form.id = formId;
    form.target = iframeName;
    form.method = "POST";
    form.action = actionUrl;

    const inputJWT = document.createElement("input");
    inputJWT.type = "hidden";
    inputJWT.name = "JWT";
    inputJWT.value = jwtToken;
    form.appendChild(inputJWT);

    document.body.appendChild(form);
    form.submit();

    iframe.onload = () => {
      iframe.style.display = "block";
    };

    window.addEventListener(
      "message",
      (event) => {
        if (event.origin === "https://centinelapistag.cardinalcommerce.com") {
          const data = JSON.parse(event.data);
          console.log("Mensaje recibido:", data);
        }
      },
      false,
    );
  }

  private removeIframe(): void {
    if (this.iframeElement) {
      this.spinner.close();
      document.body.removeChild(this.iframeElement);
    }
  }

  continueToNextStep() {
    this.spinner.open();

    const transactionIdentifier = this.uniqueCodeService.getTransactionIdentifier();

    if (!this.jwtTokenForThird || !this.transactionIdForThird) {
      console.error(
        "No se encontró jwtTokenForThird o transactionIdForThird para la tercera petición.",
      );
      this.spinner.close();
      return;
    }

    const thirdData = this.createTransactionData(transactionIdentifier, {
      auth: {
        authenticationTransactionId: this.transactionIdForThird,
        signedPares: this.jwtTokenForThird,
      },
    });

    const validateJson = { chp: this.hasPlanID, sop: thirdData, call: "validate" };
    console.log("REQUEST PARA VALIDATE AUTH", validateJson);

    this.webService
      .post(validateJson, this.webService.HOST + "/sop")
      .pipe(
        switchMap((thirdResponse) =>
          this.handleValidateAuthResponse(thirdResponse, transactionIdentifier),
        ),
        catchError((error) => this.handleErrorDuringChain(error)),
      )
      .subscribe((fourthResponse) => this.handleSaleResponse(fourthResponse));
  }

  private handleValidateAuthResponse(
    thirdResponse: any,
    transactionIdentifier: string,
  ): Observable<any> {
    this.spinner.open();
    console.log("Respuesta para VALIDATE AUTH:", thirdResponse);

    if (thirdResponse?.responseCodeDescription === "AUTHENTICATION_SUCCESSFUL") {
      console.log("Successful Step-Up Authentication ON VALIDATE AUTH");

      const extraData = thirdResponse.extraData;
      const fourthData = this.createTransactionData(transactionIdentifier, {
        auth: {
          authenticationTransactionId: this.transactionIdForThird,
        },
        eci: extraData.eci,
        eciRaw: extraData.eciRaw,
        cavv: extraData.cavv,
        authenticationTransactionId: extraData.authenticationTransactionId,
        acsTransactionId: extraData.acsTransactionId,
        paresStatus: extraData.paresStatus,
        veresEnrolled: extraData.veresEnrolled,
        xid: extraData.xid,
        ecommerceIndicator: extraData.ecommerceIndicator,
        specificationVersion: extraData.specificationVersion,
        directoryServerTransactionId: extraData.directoryServerTransactionId,
        ucafAuthenticationData: extraData.ucafAuthenticationData,
        ucafCollectionIndicator: extraData.ucafCollectionIndicator,
      });

      const saleJson = { chp: this.hasPlanID, sop: fourthData, call: "sale" };
      console.log("REQUEST PARA SALE", saleJson);

      return this.webService.post(saleJson, this.webService.HOST + "/sop");
    } else {
      console.log("VALIDATE AUTH RETORNO AUTHENTICATION_FAILED");
      this.toast.showError("AUTHENTICATION_FAILED");
      this.spinner.close();
      return of(null);
    }
  }

  private handleErrorDuringChain(error: any): Observable<null> {
    console.error("Error en la cadena de peticiones:", error);
    this.toast.showError(`Error en VALIDATE AUTH: ${error.message}`);
    this.spinner.close();
    return of(null);
  }

  private handleSaleResponse(fourthResponse: any): void {
    this.spinner.open();

    if (fourthResponse?.responseCodeDescription) {
      console.log("Respuesta del SALE:", fourthResponse);

      if (fourthResponse.responseCodeDescription === "AUTHORIZED") {
        // this.dialogRef.close(1);
        this.myLoadEvent();
      } else {
        this.spinner.close();
        this.toast.showError("El pago no se procesó correctamente. Por favor, intente nuevamente.");
      }
    } else {
      this.spinner.close();
      console.log("SALE no fue exitoso o no se ejecutó debido a un error previo.");
      this.toast.showError(
        "No se obtuvo una respuesta del proceso de pago. Verifique la transacción o intente nuevamente. " +
          fourthResponse.responseCodeDescription,
      );
    }

    this.spinner.close();
  }

  createTransactionData(transactionIdentifier: string = "", additionalData: any = {}): any {
    return {
      creditCard: {
        cardholderName: this.paymentForm.value.nameHolderCreditCard,
        creditCardDate: `${this.paymentForm.value.expYearCreditCard}${this.paymentForm.value.expMonthCreditCard}`,
        cvv: this.paymentForm.value.ccv,
      },
      transactionIdentifier: transactionIdentifier,
      currency: "USD",
      extraData: {
        ip: this.myIp,
        fingerprint: this.uniqueCode,
        screenHeight: this.myHeight,
        screenWidth: this.myWidth,
        email: this.securityService.getTokenSub(),
        street: this.paymentForm.value.street,
        city: this.paymentForm.value.city,
        postalCode: this.paymentForm.value.postalCode,
        phone: this.paymentForm.value.phone,
        ...additionalData,
      },
      auth: additionalData.auth,
    };
  }

  // TERMINA PROCESOS CYBERSOURCE

  // EMETEC PROCESOS

  private handleEmetecResponse(response: any): void {
    this.dataObject = this.buildRequestData();
    this.hasPlanID = response.id;

    if (response.sopResponse.responseCodeDescription === "transaction pending") {
      this.handleEmetecPendingTransaction(response);
    } else {
      const updatedData = this.transactionEmetec();
      updatedData.transactionIdentifier = response.sopResponse.transactionId;
      const json = { chp: response.id, sop: updatedData, call: "payment" };
      this.getEmetecPayment(json);
    }
  }

  getEmetecPayment(json: any): void {
    this.spinner.open();
    console.log("USANDO ESTA DATA HACEMOS EL PAYMENT: ", json);

    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      timer(3000)
        .pipe(
          switchMap(() => this.webService.post(json, `${this.webService.HOST}/sop`)),
          takeWhile(() => this.retryCount <= this.maxRetries),
        )
        .subscribe(
          (response) => {
            console.log("RETRY RESPONSE PAYMENT: ", response);

            if (response.responseCodeDescription === "Transaction succeeded") {
              this.spinner.open();
              this.myLoadEvent();
            } else if (response.responseCodeDescription === "transaction pending") {
              // Continue retrying
              this.getEmetecPayment(json);
            } else {
              this.spinner.close();
              this.toast.showError(
                "Hubo un error en el proceso: " + response.responseCodeDescription,
              );
            }
          },
          (err) => {
            console.error("ERROR: ", err);
            this.spinner.close();
            this.toast.showError("Error en el proceso de pago. Intente nuevamente.");
          },
        );
    } else {
      this.spinner.close();
      this.toast.showError(
        "Se alcanzó el número máximo de intentos. Transacción sigue en estatus pending. Contacte con el administrador.",
      );
    }
  }

  private handleEmetecPendingTransaction(response: any): void {
    this.spinner.open();
    // Store transaction details from the response
    this.transactionId = response.sopResponse.transactionId;
    this.iframeValue = response.sopResponse.auth3DSDetail.iframeValue;
    this.iframeName = response.sopResponse.auth3DSDetail.iframeName;
    this.iframeUrl = response.sopResponse.auth3DSDetail.iframeUrl;
    this.redirectUrl = response.sopResponse.auth3DSDetail.redirectUrl;

    console.log("Datos almacenados:");
    console.log("transactionId:", this.transactionId);
    console.log("iframeValue:", this.iframeValue);
    console.log("iframeName:", this.iframeName);
    console.log("iframeUrl:", this.iframeUrl);
    console.log("redirectUrl:", this.redirectUrl);

    // Send POST request using fetch with no-cors for the first iframe
    this.sendIframeRequest(this.iframeUrl, this.iframeValue)
      .then(() => {
        console.log("Solicitud del primer iframe enviada con éxito");
        this.spinner.close(); // Stop the loader before opening the popup

        // Open the second iframe in a popup
        this.openInteractiveIframePopup(
          this.redirectUrl,
          this.iframeValue,
          response.sopResponse.transactionId,
        );
      })
      .catch((err) => {
        console.error("Error al enviar la solicitud del primer iframe:", err);
        this.toast.showError("Error al enviar la solicitud del primer iframe:" + err);
        this.spinner.close();
      });
  }

  openInteractiveIframePopup(url: string, iframeValue: string, transactionID: string): void {
    // const dialogRef = this.dialog.open(InteractiveIframeDialog, {
    //   width: '600px',
    //   data: { url, iframeValue }
    // });
    //
    // // Store the transaction identifier in the data object
    // this.dataObject.transactionIdentifier = transactionID;
    //
    // dialogRef.afterClosed().subscribe(result => {
    //   console.log('Interactive iframe popup closed');
    //   const json = { chp: this.hasPlanID, sop: this.dataObject, call: "payment" };
    //   this.getEmetecPayment(json);
    // });
  }

  private buildRequestData(): any {
    const baseData = {
      creditcard: {
        numberCreditCard: this.paymentForm.value.number_tdc.trim(),
        nameHolderCreditCard: this.paymentForm.value.name_tdc,
        expMonthCreditCard: this.paymentForm.value.month,
        expYearCreditCard: this.paymentForm.value.year,
        ccv: this.paymentForm.value.ccv,
      },
      extraData: {
        ip: this.myIp,
        fingerprint: this.uniqueCode,
        screenHeight: this.myHeight,
        screenWidth: this.myWidth,
        email: this.securityService.getTokenSub(),
        street: this.paymentForm.value.street,
        city: this.paymentForm.value.city,
        postalCode: this.paymentForm.value.postalCode,
        phone: this.paymentForm.value.phone,
      },
    };

    return baseData;
  }

  async sendIframeRequest(url: string, iframeValue: string): Promise<void> {
    // Create the request body in URL-encoded format
    const body = new URLSearchParams();
    body.append("threeDSMethodData", iframeValue);

    console.log("Datos que se envían al iframe:");
    console.log("URL:", url);
    console.log("Datos del formulario:", body.toString());

    // Send the POST request to the iframeUrl
    try {
      let response: Response;
      let attempts = 0;
      const maxAttempts = 5;
      const delay = 1000; // 1 second

      do {
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
          mode: "no-cors", // Add no-cors mode
          credentials: "include",
        });

        if (response.status === 200) {
          console.log("Solicitud iframe enviada con éxito.");
          break;
        } else {
          console.error(
            `Intento ${attempts + 1}: Error en la solicitud iframe:`,
            response.status,
            response.statusText,
          );
        }

        attempts++;
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, delay)); // Wait 1 second before the next attempt
        }
      } while (attempts < maxAttempts);

      if (attempts === maxAttempts && response.status !== 200) {
        console.error("Se alcanzó el número máximo de intentos y la solicitud no fue exitosa.");
      }
    } catch (error) {
      console.error("Error al enviar la solicitud del primer iframe:", error);
    }
  }

  private transactionEmetec(): any {
    return {
      creditCard: {
        cardholderName: this.paymentForm.value.nameHolderCreditCard,
        creditCardDate:
          this.paymentForm.value.expYearCreditCard + this.paymentForm.value.expMonthCreditCard,
        cvv: this.paymentForm.value.ccv,
      },
      transactionIdentifier: this.uniqueCodeService.getTransactionIdentifier(),
      currency: "USD",
      extraData: {
        ip: this.myIp,
        fingerprint: this.uniqueCode,
        screenHeight: this.myHeight,
        screenWidth: this.myWidth,
        email: this.securityService.getTokenSub(),
        street: this.paymentForm.value.street,
        city: this.paymentForm.value.city,
        postalCode: this.paymentForm.value.postalCode,
        phone: this.paymentForm.value.phone,
      },
    };
  }

  // TERMINA PROCESOS PARA EMETEC

  /**
   * Único punto de acceso al endpoint de búsqueda de cliente. El backend SIEMPRE
   * filtra por code_customer (ver Customers.findByCode) sin importar que el valor
   * tipeado sea un email o un código externo — por diseño, no se agrega fallback
   * a /customer/email/.
   */
  private resolveCustomerByCode(searchValue: string, partnerId: number): Observable<any> {
    return this.webService
      .get(this.webService.HOST + "/partner/" + partnerId + "/customer/code/" + searchValue)
      .pipe(map((response) => (response.result && response.result.id != null ? response.result : null)));
  }

  /**
   * Sincroniza el clientForm completo con los datos de un customer resuelto
   * (por code_customer). Se usa tanto en search() como en buscarClienteYTarjetas()
   * cuando este último resuelve un cliente distinto al que ya estaba en el form —
   * evita que el form quede mezclado entre dos clientes distintos.
   */
  private aplicarClienteAlFormulario(customer: any): void {
    this.clientForm.patchValue({
      name_customer: customer.name_customer,
      lastName_customer: customer.lastName_customer,
      identifier_customer: customer.identifier_customer,
      email_customer: customer.email_customer,
      phoneNumber_customer: customer.phoneNumber_customer,
      address: customer.adress_customer || "",
      code_customer: customer.code_customer,
    });

    this.captureCustomerFieldsReadOnlyState();
    this.markInvalidCustomerSearchFieldsAsTouched();

    // Marcar el teléfono como "touched" para mostrar validación inmediatamente
    this.clientForm.controls["phoneNumber_customer"].markAsTouched();
    this.clientForm.controls["phoneNumber_customer"].updateValueAndValidity();
  }

  search(): void {
    const searchValue = (this.customerSearchControl.value || "").trim();
    this.customerSearchControl.setValue(searchValue, { emitEvent: false });

    if (this.customerSearchControl.invalid) {
      this.customerSearchControl.markAsTouched();
      return;
    }

    const partnerId = this.clientForm.value.partner.id;
    const isEmailSearch = AddClientComponent.EMAIL_PATTERN.test(searchValue);
    this.spinner.open();

    this.resolveCustomerByCode(searchValue, partnerId)
      .pipe(
        switchMap((customer) => {
          if (!customer) {
            return of({ customer: null, cards: [] });
          }

          this.aplicarClienteAlFormulario(customer);
          this.customerForCards = customer;
          this.mostrarFormularioCompleto = true;

          return this.creditCardHelper
            .getValidCustomerCards(customer.id)
            .pipe(
              map((cards) => ({ customer, cards })),
              catchError((error) => {
                this.limpiarSoloTarjetas();
                this.toast.showError(error.message);
                return of({ customer, cards: null });
              }),
            );
        }),
      )
      .subscribe(
        ({ customer, cards }) => {
          this.spinner.close();
          if (customer && cards !== null) {
            this.aplicarTarjetasEncontradas(cards);
            this.toast.showSuccess("Cliente encontrado,datos cargados.");
            this.clearCustomerSearch();
            this.setSearchPanelState(false);
          } else {
            if (!customer) {
              this.limpiarDatosTarjetas();
              this.limpiarDatosCustomerNoResuelto(searchValue, isEmailSearch);
              this.toast.showError(this.translate.instant("addClient.messages.customerNotFoundByCode"));
            }
          }
          this.mostrarFormularioCompleto = true;
        },
        (error) => {
          this.spinner.close();
          this.toast.showError(error.message);
          console.log(error);
        },
      );
  }

  onCustomerSearchEnter(event: KeyboardEvent): void {
    event.preventDefault();
    this.search();
  }

  clearCustomerSearch(): void {
    this.customerSearchControl.reset("");
    this.customerSearchControl.markAsPristine();
    this.customerSearchControl.markAsUntouched();
  }

  private aplicarTarjetasEncontradas(tarjetasValidas: any[]): void {
    this.tarjetasGuardadas = tarjetasValidas;
    console.log(`💳 ${tarjetasValidas.length} tarjeta(s) válida(s) encontrada(s)`);

    if (this.tarjetasGuardadas.length > 0) {
      this.usarTarjetaGuardada = true;
      this.tarjetaSeleccionadaId = this.tarjetasGuardadas[0].id;

      const primeraCard = this.tarjetasGuardadas[0];
      const mask = primeraCard.maskCreditCard || primeraCard.mask_card || "****";
      const provider = primeraCard.creditCardProvider?.description || "";
      console.log(`✓ Tarjeta preseleccionada: ${mask} (${provider})`);
    } else {
      this.usarTarjetaGuardada = false;
      this.tarjetaSeleccionadaId = null;
      console.log("ℹ️ Cliente sin tarjetas guardadas");
    }
  }

  /**
   * Resuelve el cliente asociado al valor actual del campo de búsqueda y carga
   * sus tarjetas. El correo del formulario nunca inicia búsquedas; solo se
   * reutiliza el cliente seleccionado por el buscador independiente.
   * @returns Observable que emite el customer (o null si no se encontró/no hay valor)
   */
  buscarClienteYTarjetas(): Observable<any> {
    if (this.customerForCards) {
      console.log("📦 Usando customer seleccionado por el buscador independiente");
      return this.creditCardHelper.getValidCustomerCards(this.customerForCards.id).pipe(
        map((tarjetasValidas) => {
          this.aplicarTarjetasEncontradas(tarjetasValidas);
          return this.customerForCards;
        }),
        catchError((error) => {
          console.log("❌ Error al cargar tarjetas (caché):", error);
          this.limpiarSoloTarjetas();
          return of(null);
        }),
      );
    }

    return of(null);
  }

  /**
   * El resultado de una nueva búsqueda no identificó un customer. Por lo tanto,
   * descarta los datos personales previamente resueltos antes de continuar en modo creación.
   */
  private limpiarDatosCustomerNoResuelto(searchValue: string, isEmailSearch: boolean): void {
    this.clientForm.patchValue({
      name_customer: "",
      lastName_customer: "",
      identifier_customer: "",
      email_customer: isEmailSearch ? searchValue : "",
      code_customer: isEmailSearch ? "" : searchValue,
      phoneNumber_customer: "",
      address: "",
    });
  }

  private captureCustomerFieldsReadOnlyState(): void {
    AddClientComponent.CUSTOMER_FIELDS_WITH_CONDITIONAL_READONLY.forEach((controlName) => {
      const control = this.clientForm.get(controlName);
      control?.updateValueAndValidity({ emitEvent: false });
      this.customerFieldsReadOnly[controlName] = control?.valid === true;
    });

    const emailCustomerControl = this.clientForm.get(AddClientComponent.CUSTOMER_EMAIL_FIELD);
    const emailCustomerValue = emailCustomerControl?.value;
    this.customerFieldsReadOnly[AddClientComponent.CUSTOMER_EMAIL_FIELD] =
      emailCustomerControl?.valid === true &&
      typeof emailCustomerValue === "string" &&
      emailCustomerValue.trim().length > 0;
  }

  private resetCustomerFieldsReadOnlyState(): void {
    AddClientComponent.CUSTOMER_FIELDS_WITH_CONDITIONAL_READONLY.forEach((controlName) => {
      this.customerFieldsReadOnly[controlName] = false;
    });
  }

  /**
   * Verifica si el cliente ya tiene una suscripción activa al plan seleccionado
   * @param customerId ID del cliente
   * @param planId ID del plan a verificar
   * @returns Observable<boolean> - true si ya tiene el plan activo, false si no
   */
  verificarPlanDuplicado(customerId: number, planId: number): Observable<boolean> {
    const partnerId = this.clientForm.value.partner.id;
    const url = `${this.webService.HOST}/hasplan/partner/${partnerId}/customer/${customerId}`;

    console.log(`🔍 Verificando plan duplicado - Cliente: ${customerId}, Plan: ${planId}`);

    return this.webService.get(url).pipe(
      map((response) => {
        // Verificar si el cliente YA tiene este plan activo
        const tienePlanActivo = response.result.some(
          (suscription: any) =>
            suscription.plan?.id === planId && suscription.status_customerPlan === 1, // 1 = activo
        );

        if (tienePlanActivo) {
          console.log("⚠️ Cliente YA tiene este plan activo");
        } else {
          console.log("✅ Cliente NO tiene este plan activo - puede suscribirse");
        }

        return tienePlanActivo;
      }),
      catchError((error) => {
        console.error("❌ Error al verificar plan duplicado:", error);
        // En caso de error, retornar false para no bloquear el proceso
        return of(false);
      }),
    );
  }

  /**
   * Limpia los datos de tarjetas guardadas e invalida el caché
   */
  limpiarDatosTarjetas(): void {
    this.customerForCards = null;
    this.resetCustomerFieldsReadOnlyState();
    this.tarjetasGuardadas = [];
    this.usarTarjetaGuardada = false;
    this.tarjetaSeleccionadaId = null;
    console.log("🗑️ Datos de tarjetas limpiados");
  }

  /**
   * Limpia SOLO el estado de tarjetas, sin olvidar la identidad del cliente ya
   * resuelto (customerForCards). Se usa ante errores transitorios al pedir las
   * tarjetas de un cliente que sí se resolvió correctamente — un fallo de red
   * momentáneo no significa que el cliente haya dejado de existir.
   */
  private limpiarSoloTarjetas(): void {
    this.tarjetasGuardadas = [];
    this.usarTarjetaGuardada = false;
    this.tarjetaSeleccionadaId = null;
  }
}

// @Component({
//   selector: 'interactive-iframe-dialog',
//   template: `
//     <h1 mat-dialog-title>Complete la verificación</h1>
//     <div mat-dialog-content>
//       <iframe [src]="sanitizer.bypassSecurityTrustResourceUrl(data.url)" width="100%" height="400px" sandbox="allow-forms allow-pointer-lock allow-popups allow-same-origin allow-scripts"></iframe>
//     </div>
//     <div mat-dialog-actions>
//       <button mat-button (click)="onClose()">Cerrar</button>
//     </div>
//   `,
//   styles: []
// })
// export class InteractiveIframeDialog {
//   constructor(
//     public dialogRef: MatDialogRef<InteractiveIframeDialog>,
//     @Inject(MAT_DIALOG_DATA) public data: { url: string },
//     public sanitizer: DomSanitizer
//   ) {}
//
//   onClose(): void {
//     this.dialogRef.close();
//     console.log("AQUI DEBERIA IR EL SALE")
//   }
// }
